import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { GroupsRounded } from '@mui/icons-material';
import { collection, documentId, getDocs, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import { useTenantStore } from '../../../stores/tenant.store';
import {
  programEnrollmentApi,
  type ProgramEnrollmentStatus,
} from '../../../lib/api/program-enrollment.api';
import {
  personDisplayName,
  sortTermsNewestFirst,
  ymdOf,
  type CohortTermOption,
} from './cohort-tab.model';

const STATUS_COLOR: Record<ProgramEnrollmentStatus, 'success' | 'warning' | 'info' | 'default'> = {
  active: 'success',
  on_leave: 'warning',
  graduated: 'info',
  withdrawn: 'default',
};

interface Option {
  id: string;
  name: string;
}

export function CohortTab(): JSX.Element {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();

  const [programs, setPrograms] = useState<Option[]>([]);
  const [terms, setTerms] = useState<CohortTermOption[]>([]);
  const [programId, setProgramId] = useState('');
  const [intakeTermId, setIntakeTermId] = useState('');

  // Active programs.
  useEffect(() => {
    if (!currentTenantId) return;
    return onSnapshot(collection(db, `tenants/${currentTenantId}/programs`), (snap) => {
      setPrograms(
        snap.docs
          .filter((d) => ((d.data()['status'] as string | undefined) ?? 'active') === 'active')
          .map((d) => ({ id: d.id, name: (d.data()['name'] as string) ?? d.id })),
      );
    });
  }, [currentTenantId]);

  // Períodos, del más nuevo al más viejo (antes: en el orden de sus ids).
  useEffect(() => {
    if (!currentTenantId) return;
    return onSnapshot(collection(db, `tenants/${currentTenantId}/terms`), (snap) => {
      setTerms(
        sortTermsNewestFirst(
          snap.docs.map((d) => {
            const startDate = ymdOf(d.data()['startDate']);
            return {
              id: d.id,
              name: (d.data()['name'] as string) ?? d.id,
              ...(startDate ? { startDate } : {}),
            };
          }),
        ),
      );
    });
  }, [currentTenantId]);

  const cohortQ = useQuery({
    queryKey: ['cohort', currentTenantId, programId, intakeTermId],
    enabled: !!currentTenantId && !!programId && !!intakeTermId,
    queryFn: () =>
      programEnrollmentApi.listProgramCohort({
        tenantId: currentTenantId!,
        programId,
        intakeTermId,
      }),
    staleTime: 60_000,
  });

  const items = useMemo(() => cohortQ.data?.items ?? [], [cohortQ.data]);

  // Nombres SOLO de los alumnos de la cohorte, por id (de a 30). Antes se
  // escuchaban todas las personas del seminario (709 en labts) para esto.
  const studentIdsKey = useMemo(
    () => [...new Set(items.map((e) => e.studentId))].sort().join(','),
    [items],
  );
  const namesQ = useQuery({
    queryKey: ['cohort-names', currentTenantId, studentIdsKey],
    enabled: !!currentTenantId && !!studentIdsKey,
    staleTime: 60_000,
    queryFn: async () => {
      const ids = studentIdsKey.split(',');
      const names = new Map<string, string>();
      for (let i = 0; i < ids.length; i += 30) {
        const snap = await getDocs(
          query(
            collection(db, `tenants/${currentTenantId}/people`),
            where(documentId(), 'in', ids.slice(i, i + 30)),
          ),
        );
        for (const d of snap.docs) names.set(d.id, personDisplayName(d.id, d.data()));
      }
      return names;
    },
  });
  const peopleNames = namesQ.data ?? new Map<string, string>();

  const retention = useMemo(() => {
    const counts: Record<ProgramEnrollmentStatus, number> = {
      active: 0,
      on_leave: 0,
      graduated: 0,
      withdrawn: 0,
    };
    for (const e of items) counts[e.status]++;
    const total = items.length;
    const retained = counts.active + counts.graduated;
    return { counts, total, pct: total > 0 ? Math.round((retained / total) * 100) : 0 };
  }, [items]);

  const termName = (id: string | null) =>
    id ? (terms.find((tm) => tm.id === id)?.name ?? id) : '—';
  const selected = !!programId && !!intakeTermId;

  return (
    <Stack spacing={3}>
      {/* Filters */}
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
        <TextField
          select
          size="small"
          label={t('reports.cohort.selectProgram')}
          value={programId}
          onChange={(e) => setProgramId(e.target.value)}
          sx={{ minWidth: 240 }}
        >
          {programs.map((p) => (
            <MenuItem key={p.id} value={p.id}>
              {p.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label={t('reports.cohort.selectTerm')}
          value={intakeTermId}
          onChange={(e) => setIntakeTermId(e.target.value)}
          sx={{ minWidth: 240 }}
        >
          {terms.map((tm) => (
            <MenuItem key={tm.id} value={tm.id}>
              {tm.name}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {!selected && (
        <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
          <GroupsRounded sx={{ fontSize: 60, opacity: 0.2, mb: 2 }} />
          <Typography variant="h6">{t('reports.cohort.emptyNoSelection')}</Typography>
        </Box>
      )}

      {selected && cohortQ.isLoading && (
        <Stack alignItems="center" py={6}>
          <CircularProgress size={32} />
        </Stack>
      )}

      {selected && cohortQ.data && (
        <>
          {/* Retention summary */}
          <Grid container spacing={2}>
            <Grid item xs={6} md={2.4}>
              <Card variant="outlined" sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">
                    {t('reports.cohort.retention')}
                  </Typography>
                  <Typography variant="h5" fontWeight={700}>
                    {retention.pct}%
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            {(['active', 'graduated', 'on_leave', 'withdrawn'] as ProgramEnrollmentStatus[]).map(
              (s) => (
                <Grid item xs={6} md={2.4} key={s}>
                  <Card variant="outlined" sx={{ borderRadius: 3 }}>
                    <CardContent>
                      <Typography variant="caption" color="text.secondary">
                        {t(`reports.cohort.status.${s}`)}
                      </Typography>
                      <Typography variant="h5" fontWeight={700}>
                        {retention.counts[s]}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ),
            )}
          </Grid>

          {/* Roster */}
          {items.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
              <Typography variant="body2">{t('reports.cohort.emptyCohort')}</Typography>
            </Box>
          ) : (
            <TableContainer component={Card} variant="outlined" sx={{ borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('reports.cohort.colStudent')}</TableCell>
                    <TableCell>{t('reports.cohort.colStatus')}</TableCell>
                    <TableCell>{t('reports.cohort.colCatalog')}</TableCell>
                    <TableCell>{t('reports.cohort.colIntake')}</TableCell>
                    <TableCell>{t('reports.cohort.colExpectedGrad')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((e) => (
                    <TableRow key={e.id} hover>
                      <TableCell>{peopleNames.get(e.studentId) ?? e.studentId}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={STATUS_COLOR[e.status]}
                          label={t(`reports.cohort.status.${e.status}`)}
                        />
                      </TableCell>
                      <TableCell>{e.catalogYear ?? '—'}</TableCell>
                      <TableCell>{termName(e.intakeTermId)}</TableCell>
                      <TableCell>{termName(e.expectedGraduationTermId)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </Stack>
  );
}
